/**
 * @component NewsPopup
 * @description 관리자가 뉴스에서 '메인 팝업'으로 지정한 공지를 홈 진입 시 팝업으로 보여줍니다.
 *              노출 대상과 기간 판정은 서버(GET /api/news/popups)가 하고, 여기서는 방문자의 숨김 선택만 반영합니다.
 * @usage HomePage 하단에서 렌더링되며 화면 가운데에 고정 레이어로 뜹니다. 노출 대상이 없으면 아무것도 그리지 않습니다.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Megaphone } from 'lucide-react';

const HIDE_STORAGE_KEY = 'sh_popup_hide';
// [비즈니스] 손가락으로 이 거리(px) 이상 밀었을 때만 넘깁니다. 살짝 건드린 탭이 넘김으로 오인되지 않게 합니다.
const SWIPE_THRESHOLD = 50;

// [비즈니스] '닫기'·'자세히 보기'는 지금 떠 있는 팝업만 닫습니다. 메인 화면으로 다시 오면 팝업이 또 뜨며,
//            다시 띄우지 않는 것은 '오늘 하루 보지 않기'를 체크했을 때뿐입니다.
// [기술] 저장소를 쓸 수 없는 환경(시크릿 모드 등)에서도 '오늘 하루 보지 않기'가 이번 페이지 로드 동안은
//        지켜지도록, 저장에 실패하면 이 메모리 사본에 숨김 정보를 남깁니다.
const memoryHideMap = {};

// [기능] 방문자가 숨긴 팝업 목록({ slug: 만료시각ms })을 읽습니다. 읽을 수 없으면 빈 객체를 반환합니다.
// [기술] 시크릿 모드·저장소 차단 환경에서도 팝업은 동작해야 하므로 모든 접근을 try/catch로 감쌉니다.
function readHideMap() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(HIDE_STORAGE_KEY) || '{}');
    const stored = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    return { ...memoryHideMap, ...stored };
  } catch (error) {
    return { ...memoryHideMap };
  }
}

// [기능] 만료된 항목을 정리한 뒤 숨김 목록을 저장합니다. 저장에 실패해도 팝업 닫기는 계속 진행됩니다.
function writeHideMap(map) {
  const now = Date.now();
  const cleaned = Object.fromEntries(Object.entries(map).filter(([, expiresAt]) => expiresAt > now));
  try {
    window.localStorage.setItem(HIDE_STORAGE_KEY, JSON.stringify(cleaned));
  } catch (error) {
    // [기술] 저장소를 쓸 수 없으면 메모리 사본에 남겨 이번 페이지 로드 동안은 숨김을 지킵니다.
    Object.assign(memoryHideMap, cleaned);
  }
}

// [기능] 방문자 로컬 시각 기준으로 오늘 23:59:59.999의 타임스탬프를 반환합니다.
// [비즈니스] '오늘 하루 보지 않기'는 방문자가 보는 날짜의 자정 직전까지 숨기는 정책입니다.
function getEndOfLocalDayMs(now = new Date()) {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).getTime();
}

// [기능] 노출 중인 공지를 불러와 한 팝업 안에서 좌우로 넘겨 보는 슬라이드로 보여줍니다.
export default function NewsPopup({ onNavigate }) {
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(true);
  const [hideToday, setHideToday] = useState(false);
  const swipeStartX = useRef(null);

  useEffect(() => {
    // [기술] 응답 전에 홈을 벗어나면 언마운트된 컴포넌트의 상태를 바꾸지 않도록 막습니다.
    let isActive = true;
    fetch('/api/news/popups')
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (!isActive || !Array.isArray(data)) return;
        const hideMap = readHideMap();
        const now = Date.now();
        // [비즈니스] 방문자가 '오늘 하루 보지 않기'로 숨긴 공지는 만료 전까지 제외합니다.
        const visible = data.filter(
          (item) =>
            item &&
            typeof item.slug === 'string' &&
            typeof item.title === 'string' &&
            !(Number(hideMap[item.slug]) > now)
        );
        setItems(visible);
        setIndex(0);
      })
      .catch(() => {
        // [비즈니스] 팝업은 부가 기능이므로 조회 실패가 홈 화면 표시를 방해하지 않도록 조용히 무시합니다.
      });
    return () => {
      isActive = false;
    };
  }, []);

  // [기능] 지금 떠 있는 팝업을 닫습니다. 메인 화면에 다시 들어오면 새로 뜹니다.
  const dismiss = useCallback(() => {
    setIsOpen(false);
  }, []);

  if (!isOpen || items.length === 0) return null;

  const total = items.length;
  const current = items[Math.min(index, total - 1)];

  // [기능] 팝업을 닫습니다. '오늘 하루 보지 않기'가 체크돼 있으면 노출 중인 모든 공지를 오늘 자정 직전까지 숨깁니다.
  // [비즈니스] 여러 건이 한 팝업에 함께 뜨므로, 보고 있는 한 건이 아니라 팝업 전체를 숨기는 것이 방문자 의도에 맞습니다.
  //            체크는 '어떻게 닫든' 적용합니다. 체크하고 '자세히 보기'로 나가도 다시 뜨지 않아야 하기 때문입니다.
  const handleClose = () => {
    if (hideToday) {
      const expiresAt = getEndOfLocalDayMs();
      const map = readHideMap();
      items.forEach((item) => {
        map[item.slug] = expiresAt;
      });
      writeHideMap(map);
    }
    dismiss();
  };

  // [기능] 팝업을 닫고 지정한 공지의 뉴스 상세 페이지로 이동합니다.
  // [기술] 상세 주소는 목록과 같은 /news/<slug>.html 형식이어야 routing.js 가 해석합니다.
  const openDetail = (item) => {
    handleClose();
    onNavigate(`/news/${item.slug}.html`);
  };

  // [기능] 지정한 위치의 공지로 넘깁니다. 범위를 벗어나면 반대쪽 끝으로 순환합니다.
  const goTo = (next) => setIndex(((next % total) + total) % total);

  // [기능] 손가락·마우스로 좌우로 민 거리를 보고 이전/다음 공지로 넘깁니다.
  const handlePointerDown = (event) => {
    swipeStartX.current = event.clientX;
  };
  // [기술] 팝업 밖에서 손을 떼거나 터치가 취소되면 시작 좌표를 버려, 다음 클릭이 넘김으로 오인되지 않게 합니다.
  const resetSwipe = () => {
    swipeStartX.current = null;
  };
  const handlePointerUp = (event) => {
    if (swipeStartX.current === null) return;
    const diff = event.clientX - swipeStartX.current;
    swipeStartX.current = null;
    if (total > 1 && Math.abs(diff) > SWIPE_THRESHOLD) goTo(index + (diff < 0 ? 1 : -1));
  };

  // [비즈니스] 공지 레이어는 화면 가운데에 떠 있지만 배경을 가리지 않습니다.
  //            방문자가 팝업을 닫지 않아도 페이지를 계속 보고 조작할 수 있어야 하기 때문입니다.
  return (
    <div className="news-popup-layer">
      <section className="news-popup" role="dialog" aria-modal="false" aria-label="뉴스 팝업">
        <div
          className={`news-popup-viewport${current.image ? '' : ' is-light'}`}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerCancel={resetSwipe}
          onPointerLeave={resetSwipe}
        >
          <div className="news-popup-track" style={{ transform: `translateX(-${index * 100}%)` }}>
            {items.map((item, i) => {
              const isActive = i === index;
              return (
                <article
                  key={item.slug}
                  className={`news-popup-slide ${item.image ? 'has-image' : 'no-image'}`}
                  // [기술] 사진은 슬라이드 배경으로 쓰므로 CSS 변수로 넘겨 ::before 에서 그립니다.
                  style={item.image ? { '--slide-image': `url("${item.image}")` } : undefined}
                  aria-hidden={!isActive}
                  // [기술] 화면 밖 슬라이드의 버튼으로 키보드 포커스가 가지 않도록 막습니다.
                  inert={!isActive}
                >
                  {/* [비즈니스] 사진 없는 공지는 확성기 아이콘으로 '알림' 성격을 드러냅니다(디자인 시안). */}
                  {!item.image && (
                    <span className="news-popup-icon" aria-hidden="true">
                      <Megaphone size={26} strokeWidth={1.8} />
                    </span>
                  )}
                  <div className="news-popup-content">
                    {item.category && <span className="news-popup-eyebrow">{item.category.toUpperCase()}</span>}
                    <h2 className="news-popup-title">{item.title}</h2>
                    {/* [기술] 문구는 텍스트 노드로만 렌더링합니다. 줄바꿈은 CSS(pre-line)로 표시합니다. */}
                    {item.text && <p className="news-popup-body">{item.text}</p>}
                    <div className="news-popup-cta-wrap">
                      {!item.image && <span className="news-popup-cta-note">더 자세한 내용은 뉴스에서 확인하실 수 있습니다.</span>}
                      <button type="button" className="news-popup-detail" onClick={() => openDetail(item)}>
                        자세히 보기 <span className="news-popup-detail-arrow" aria-hidden="true">→</span>
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          {total > 1 && (
            <>
              <button type="button" className="news-popup-nav news-popup-prev" aria-label="이전 공지" onClick={() => goTo(index - 1)}>
                <ChevronLeft size={18} strokeWidth={2.2} aria-hidden="true" />
              </button>
              <button type="button" className="news-popup-nav news-popup-next" aria-label="다음 공지" onClick={() => goTo(index + 1)}>
                <ChevronRight size={18} strokeWidth={2.2} aria-hidden="true" />
              </button>
            </>
          )}
        </div>

        {/* [비즈니스] 팝업이 작아 슬라이드 위에 쪽 표시를 얹으면 글을 가리므로, 쪽 표시는 하단 바 가운데에 둡니다(디자인 시안). */}
        <footer className="news-popup-footer">
          <label className="news-popup-today">
            <input type="checkbox" checked={hideToday} onChange={(e) => setHideToday(e.target.checked)} />
            <span>오늘 하루 보지 않기</span>
          </label>
          {total > 1 && (
            <div className="news-popup-pager">
              <div className="news-popup-dots">
                {items.map((item, i) => (
                  <button
                    key={item.slug}
                    type="button"
                    className={`news-popup-dot${i === index ? ' is-active' : ''}`}
                    aria-label={`${i + 1}번째 공지`}
                    aria-current={i === index ? 'true' : undefined}
                    onClick={() => goTo(i)}
                  />
                ))}
              </div>
              <span className="news-popup-count">
                {index + 1} / {total}
              </span>
            </div>
          )}
          <button type="button" className="news-popup-close" onClick={handleClose}>
            닫기
          </button>
        </footer>
      </section>
    </div>
  );
}
